if (!customElements.get('tibhelper-cart-upgrade')) {
  class TibhelperCartUpgrade extends HTMLElement {
    static busy = false;

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.button = this.querySelector('button');
      this.button.hidden = false;
      this.button.addEventListener('click', () => this.upgrade());
    }

    async readCart() {
      const response = await fetch(`${routes.cart_url}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(this.dataset.error);
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error(this.dataset.error);
      return cart;
    }

    ordinary(item) {
      const properties = Object.entries(item?.properties || {});
      const ordinaryProperties = properties.length === 0 || (properties.length === 1 &&
        properties[0][0] === this.dataset.preorderProperty && properties[0][1] === this.dataset.preorderValue);
      return item && ordinaryProperties && !item.selling_plan_allocation &&
        !item.parent_relationship?.parent && !item.parent_relationship?.parent_key &&
        !item.item_components?.length && item.instructions?.can_remove !== false &&
        item.instructions?.can_update_quantity !== false;
    }

    productLines(cart) {
      return cart.items.filter((item) => String(item.product_id) === this.dataset.productId);
    }

    upgradeTarget(item) {
      if (this.ordinary(item)) return true;
      if (this.dataset.productId !== '9932647629079' || this.dataset.sourceId !== '54675778273559' ||
        this.dataset.targetId !== '54675778306327' || String(item?.variant_id) !== this.dataset.targetId ||
        !Array.isArray(item.item_components) || item.item_components.length !== 1 ||
        !this.ordinary({ ...item, item_components: [] })) return false;
      const component = item.item_components[0];
      return String(component?.variant_id) === this.dataset.sourceId && component.quantity === 2 &&
        !component.selling_plan_allocation && !component.item_components?.length &&
        Object.entries(component.properties || {}).every(([key, value]) => item.properties?.[key] === value);
    }

    cartState(cart) {
      const lines = this.productLines(cart);
      const source = lines.find((item) => String(item.variant_id) === this.dataset.sourceId && this.ordinary(item));
      const target = lines.find((item) => String(item.variant_id) === this.dataset.targetId && this.upgradeTarget(item));
      return {
        source,
        target,
        sourceOnly: lines.length === 1 && source?.quantity === 1,
        targetOnly: lines.length === 1 && target?.quantity === 1,
        sourceAndTarget: lines.length === 2 && source?.quantity === 1 && target?.quantity === 1,
      };
    }

    eligibleSource(cart) {
      const state = this.cartState(cart);
      return state.sourceOnly && state.source.key === this.dataset.sourceKey ? state.source : null;
    }

    pendingQuantityEdit() {
      return [...document.querySelectorAll('cart-items input[name="updates[]"], cart-drawer-items input[name="updates[]"]')]
        .some((input) => input.value !== input.getAttribute('value'));
    }

    sections() {
      const sections = new Map();
      document.querySelectorAll('cart-items, cart-drawer-items').forEach((surface) => {
        surface.getSectionsToRender?.().forEach((item) => {
          const section = item.section || item.id;
          if (section) sections.set(section, { ...item, section });
        });
      });
      return [...sections.values()];
    }

    async sectionMarkup(sections) {
      const url = new URL(routes.cart_url, window.location.origin);
      url.searchParams.set('sections', sections.map((section) => section.section).join(','));
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(this.dataset.review);
      return response.json();
    }

    async write(url, body, action, line) {
      const EventClass = window.StandardEvents?.CartLinesUpdateEvent;
      const deferred = EventClass?.createPromise();
      let mutationConfirmed = false;
      if (deferred) {
        deferred.promise.catch(() => {});
        this.dispatchEvent(new EventClass({ action, context: 'cart', lines: [line], promise: deferred.promise }));
      }
      try {
        const response = await fetch(url, { ...fetchConfig(), body: JSON.stringify(body) });
        const result = await response.json();
        if (!response.ok || result.status || result.errors) {
          const error = new Error(this.dataset.error);
          const status = Number(result.status || response.status);
          error.cartMutation = status >= 400 && status < 500 ? 'rejected' : 'uncertain';
          throw error;
        }
        mutationConfirmed = true;
        const cart = await this.readCart();
        deferred?.resolve({ cart: EventClass.createCartFromAjaxResponse(cart) });
        return cart;
      } catch (error) {
        if (!error.cartMutation) error.cartMutation = mutationConfirmed ? 'confirmed' : 'uncertain';
        deferred?.reject(error);
        throw error;
      }
    }

    lock() {
      window.TibhelperCartUpgradeBusy = true;
      this.blockEvent = (event) => {
        if (event.target.closest('cart-drawer .drawer__close, #CartDrawer-Overlay')) return;
        if (event.target.closest('cart-items, cart-drawer, #main-cart-footer, product-form, cart-note')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      ['click', 'input', 'change', 'submit'].forEach((type) => document.addEventListener(type, this.blockEvent, true));
      this.disabledControls = [...document.querySelectorAll('cart-items button, cart-items input, cart-drawer button:not(.drawer__close), cart-drawer input, cart-drawer textarea, #main-cart-footer button, product-form button')]
        .map((element) => ({ element, disabled: element.disabled }));
      this.disabledControls.forEach(({ element }) => { element.disabled = true; });
      document.querySelectorAll('tibhelper-cart-upgrade').forEach((element) => element.setAttribute('aria-busy', 'true'));
      const drawer = document.querySelector('cart-drawer.active');
      const close = drawer?.querySelector('.drawer__close');
      if (drawer && close) trapFocus(drawer.querySelector('.drawer__inner'), close);
    }

    unlock() {
      window.TibhelperCartUpgradeBusy = false;
      ['click', 'input', 'change', 'submit'].forEach((type) => document.removeEventListener(type, this.blockEvent, true));
      this.disabledControls?.forEach(({ element, disabled }) => { if (element.isConnected) element.disabled = disabled; });
      document.querySelectorAll('tibhelper-cart-upgrade').forEach((element) => element.removeAttribute('aria-busy'));
      TibhelperCartUpgrade.busy = false;
    }

    async refresh(cart, sections) {
      const markup = await this.sectionMarkup(sections);
      const replacements = sections.map((section) => {
        const root = document.getElementById(section.id);
        const target = root?.querySelector(section.selector) || root;
        const html = markup[section.section];
        const source = html && new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (!target || !source) throw new Error(this.dataset.review);
        return { target, source };
      });
      replacements.forEach(({ target, source }) => { target.innerHTML = source.innerHTML; });
      document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer').forEach((element) => element.classList.toggle('is-empty', cart.item_count === 0));
      await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    }

    announce(message, success) {
      const drawer = document.querySelector('cart-drawer');
      const scope = this.dataset.surface === 'drawer' ? drawer : document.getElementById('main-cart-footer');
      const globalStatus = document.getElementById('TibhelperCartUpgradeStatus');
      const drawerActive = drawer?.classList.contains('active');
      const status = this.dataset.surface === 'drawer'
        ? document.getElementById('CartDrawer-CartErrors')
        : document.getElementById('cart-errors');
      if (globalStatus) {
        globalStatus.setAttribute('role', success ? 'status' : 'alert');
        globalStatus.textContent = !status || (this.dataset.surface === 'drawer' && !drawerActive) ? message : '';
      }
      if (status) {
        status.setAttribute('role', success ? 'status' : 'alert');
        status.textContent = message;
      } else {
        const local = scope?.querySelector('.tibhelper-cart-upgrade__status');
        if (local) local.textContent = message;
      }
      const focus = scope?.querySelector('tibhelper-cart-upgrade button') ||
        (this.dataset.surface === 'drawer' ? drawer : document.querySelector('cart-items'))?.querySelector('.cart-item__name');
      if (drawerActive && this.dataset.surface === 'drawer') {
        trapFocus(drawer.querySelector('.drawer__inner'), focus || drawer.querySelector('.drawer__close'));
      } else if (this.dataset.surface !== 'drawer') {
        focus?.focus({ preventScroll: true });
      }
    }

    async upgrade() {
      if (TibhelperCartUpgrade.busy) return;
      if (this.pendingQuantityEdit() || document.querySelector('.cart__items--disabled, product-form .loading, product-form [aria-disabled="true"]')) {
        this.querySelector('[role="status"]').textContent = this.dataset.busy;
        return;
      }
      TibhelperCartUpgrade.busy = true;
      this.lock();
      this.querySelector('[role="status"]').textContent = this.dataset.busy;
      let sections = [];
      let cart;
      let success = false;
      let message = this.dataset.error;
      let mutationStarted = false;
      let addStatus = 'not-started';
      try {
        sections = this.sections();
        if (!sections.length || sections.length > 5) throw new Error(this.dataset.review);
        cart = await this.readCart();
        const sourceItem = this.eligibleSource(cart);
        if (!sourceItem) throw new Error(this.dataset.review);
        const markup = await this.sectionMarkup(sections);
        const currentOffer = Object.values(markup).filter(Boolean).some((html) => {
          const document = new DOMParser().parseFromString(html, 'text/html');
          return [...document.querySelectorAll('tibhelper-cart-upgrade')].some((element) =>
            element.dataset.sourceKey === this.dataset.sourceKey && element.dataset.targetId === this.dataset.targetId);
        });
        if (!currentOffer) throw new Error(this.dataset.review);
        mutationStarted = true;
        try {
          cart = await this.write(routes.cart_add_url, { items: [{ id: this.dataset.targetId, quantity: 1, properties: sourceItem.properties || {} }] }, 'add', { merchandiseId: this.dataset.targetId, quantity: 1 });
          addStatus = 'confirmed';
        } catch (error) {
          addStatus = error.cartMutation || 'uncertain';
          cart = await this.readCart();
        }
        let state = this.cartState(cart);

        if (state.targetOnly) {
          success = addStatus === 'confirmed';
          message = success ? this.dataset.success : this.dataset.review;
        } else if (state.sourceAndTarget) {
          let removeStatus = 'uncertain';
          try {
            cart = await this.write(routes.cart_change_url, { id: state.source.key, quantity: 0 }, 'remove', { id: state.source.key, quantity: 0 });
            removeStatus = 'confirmed';
          } catch (error) {
            removeStatus = error.cartMutation || 'uncertain';
            cart = await this.readCart();
          }
          state = this.cartState(cart);

          if (state.targetOnly) {
            success = true;
          } else if (state.sourceAndTarget && removeStatus === 'rejected' && addStatus === 'confirmed') {
            // The target was absent before this operation, so it is safe to remove
            // only this newly added line after a definitive source rejection.
            try {
              cart = await this.write(routes.cart_change_url, { id: state.target.key, quantity: 0 }, 'remove', { id: state.target.key, quantity: 0 });
            } catch {
              cart = await this.readCart();
            }
            state = this.cartState(cart);
            message = state.sourceOnly ? this.dataset.unchanged : this.dataset.review;
          } else {
            message = state.sourceOnly && removeStatus === 'rejected'
              ? this.dataset.unchanged
              : this.dataset.review;
          }
        } else {
          if (addStatus === 'confirmed' && state.target?.quantity === 1) {
            // A local or external quantity edit changed eligibility after preflight.
            // Remove only the target confirmed as added by this operation.
            try {
              cart = await this.write(routes.cart_change_url, { id: state.target.key, quantity: 0 }, 'remove', { id: state.target.key, quantity: 0 });
            } catch {
              cart = await this.readCart();
            }
            state = this.cartState(cart);
          }
          message = addStatus === 'rejected' && state.sourceOnly ? this.dataset.unchanged : this.dataset.review;
        }

        if (success) message = this.dataset.success;
      } catch (error) {
        try { cart = await this.readCart(); } catch { cart = null; }
        const state = cart ? this.cartState(cart) : null;
        if (!mutationStarted && state?.sourceOnly) {
          message = this.dataset.unchanged;
        } else {
          message = mutationStarted ? this.dataset.review : error.message;
        }
      } finally {
        try {
          if (!cart) throw new Error(this.dataset.review);
          await this.refresh(cart, sections);
        } catch {
          this.unlock();
          window.location.assign(routes.cart_url);
          return;
        }
        this.unlock();
        this.announce(message, success);
      }
    }
  }
  customElements.define('tibhelper-cart-upgrade', TibhelperCartUpgrade);
}
